import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { PROGRAMS, SCHOOL_TIERS } from "@/lib/programs";

const program = PROGRAMS.find((p) => p.slug === "school");

export default function SchoolProgram() {
    return (
        <div data-testid="school-program-page" className="bg-brand-obsidian">
            {/* HERO */}
            <section className="relative min-h-[80svh] flex items-end overflow-hidden">
                <img
                    src={program.image}
                    alt={program.title}
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/70" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_85%,rgba(31,45,85,0.55),transparent_55%),radial-gradient(circle_at_85%_15%,rgba(230,0,0,0.18),transparent_50%)] pointer-events-none" />
                <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-10 pt-32 pb-16 md:pb-24">
                    <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-5 inline-flex items-center gap-3">
                        <span className="inline-block w-10 h-px bg-brand-red" />
                        Program · Ages 8 — 18
                    </div>
                    <h1 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-wider leading-[0.88] text-white max-w-6xl">
                        ELITE YOUTH <span className="text-brand-red">PREPARATION.</span>
                    </h1>
                    <p className="mt-7 text-zinc-300 text-lg max-w-2xl leading-relaxed">
                        {program.summary}
                    </p>
                </div>
            </section>

            {/* QUALITIES STRIP */}
            <section className="py-14 md:py-16 border-t border-zinc-900 bg-black">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-4 inline-flex items-center gap-2">
                        <span className="inline-block w-8 h-px bg-brand-red" />
                        The Five Qualities We Coach
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-zinc-900 border border-zinc-900 mt-6">
                        {[
                            { label: "Speed", desc: "Stride, mechanics, max velocity." },
                            { label: "Strength", desc: "Force from the ground up." },
                            { label: "Elasticity", desc: "Stretch-shortening cycle, whip." },
                            { label: "Power", desc: "Strength expressed at speed." },
                            { label: "Durability", desc: "Tissue tolerance & recovery." },
                        ].map((q) => (
                            <div key={q.label} className="bg-black px-5 py-6">
                                <div className="font-display text-2xl tracking-wider text-white">{q.label}</div>
                                <div className="text-xs text-zinc-300 mt-1.5 leading-relaxed">{q.desc}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* THREE STAGE CARDS */}
            <section className="py-20 md:py-28 border-t border-zinc-900">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
                        <div className="md:col-span-7">
                            <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-4 inline-flex items-center gap-2">
                                <span className="inline-block w-8 h-px bg-brand-red" />
                                Three Stages — One Athlete
                            </div>
                            <h2 className="font-display text-4xl md:text-6xl tracking-wider text-white leading-[0.95]">
                                COACHED FOR
                                <br />THE STAGE THEY'RE IN.
                            </h2>
                        </div>
                        <div className="md:col-span-5">
                            <p className="text-zinc-200 leading-relaxed">
                                Kids and teens don't all need the same thing. The youth development window splits cleanly into three
                                developmental stages — each one engineered around the qualities that stage can actually absorb.
                            </p>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-5 md:gap-6">
                        {SCHOOL_TIERS.map((tier, i) => (
                            <motion.div
                                key={tier.slug}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.08 }}
                            >
                                <Link
                                    to={tier.path}
                                    data-testid={`school-tier-card-${tier.slug}`}
                                    className="group relative p-8 border border-zinc-900 bg-gradient-to-br from-brand-navy-950 via-zinc-950 to-zinc-950 hover:border-brand-red transition-colors duration-300 block h-full overflow-hidden"
                                >
                                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_100%_0%,rgba(31,45,85,0.35),transparent_55%)] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                                    <div className="relative flex items-start justify-between">
                                        <div className="font-display text-5xl text-brand-red tracking-wider">
                                            {tier.ageRange}
                                        </div>
                                        <ArrowUpRight className="w-5 h-5 text-zinc-200 group-hover:text-brand-red transition-colors" />
                                    </div>
                                    <div className="relative text-[10px] tracking-[0.25em] uppercase text-zinc-300 mt-5">
                                        {tier.eyebrow}
                                    </div>
                                    <div className="relative mt-2 font-display text-2xl tracking-wider text-white leading-tight">
                                        {tier.title}
                                    </div>
                                    <div className="relative text-[11px] tracking-[0.18em] uppercase text-zinc-200 mt-2">
                                        {tier.focus}
                                    </div>
                                    <p className="relative mt-5 text-sm text-zinc-200 leading-relaxed">
                                        {tier.headline}
                                    </p>
                                    <div className="relative mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-brand-red">
                                        Open stage <ArrowRight className="w-3.5 h-3.5" />
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* OTHER PROGRAMS CTA */}
            <OtherProgramsCTA currentSlug="school" />
        </div>
    );
}

export function OtherProgramsCTA({ currentSlug }) {
    const others = PROGRAMS.filter((p) => p.slug !== currentSlug);
    return (
        <section className="relative py-20 md:py-24 bg-brand-obsidian overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_0%_50%,rgba(31,45,85,0.28),transparent_55%),radial-gradient(circle_at_100%_50%,rgba(230,0,0,0.10),transparent_55%)] pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-6 md:px-10">
                <div className="flex items-end justify-between mb-10">
                    <h2 className="font-display text-3xl md:text-5xl tracking-wider text-white leading-tight">
                        OTHER PROGRAMS
                    </h2>
                    <Link to="/" className="btn-ghost-tactical hidden md:inline-flex">
                        Back to home <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
                <div className="grid md:grid-cols-2 gap-5 md:gap-6">
                    {others.map((p) => (
                        <Link
                            key={p.slug}
                            to={p.path}
                            data-testid={`other-program-${p.slug}`}
                            className="group relative block overflow-hidden border border-zinc-900 hover:border-brand-red transition-colors"
                        >
                            <div className="relative aspect-[16/8] overflow-hidden bg-zinc-950">
                                <img
                                    src={p.image}
                                    alt={p.title}
                                    className="absolute inset-0 w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between">
                                    <div>
                                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300">
                                            {p.eyebrow}
                                        </div>
                                        <div className="font-display text-3xl md:text-4xl tracking-wider text-white">
                                            {p.title}
                                        </div>
                                    </div>
                                    <ArrowUpRight className="w-6 h-6 text-white group-hover:text-brand-red transition-colors" />
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}
